import { addDays, daysBetween, isoWeekStart, type IsoDate } from '../dates';
import { planRequirements, type Plan } from '../plan';
import { productOf } from '../price-book';
import { shoppingTotals, type ShoppingResult, type ShoppingTrip } from '../shopping';
import { addLot, stockFromPantry, takeFromStock, type Stock } from '../stock';
import type { Product } from '../types';
import { generatePlan } from './generate';
import { shopForPlan, type CarryOver, type PlanRequest } from './request';

/**
 * DE: Was feststeht: heute, erledigte Einkäufe (vor heute oder als gekauft markiert) und die
 * Rücklagen des Plans (Notfallvorrat).
 * EN: What is fixed: today, done trips (before today or marked bought) and the plan's reserves
 * (backup kit).
 */
export interface DoneState {
  today: IsoDate;
  doneTrips: readonly ShoppingTrip[];
  reserved: readonly { date: IsoDate; recipeId: string }[];
  /**
   * DE: Frühester Einkaufstag, falls vor `today` (Plan, der erst später beginnt).
   * EN: Earliest shopping day when it is before `today` (a plan that starts later).
   */
  earliestTrip?: IsoDate;
}

function addTrip(stock: Stock, trip: ShoppingTrip, catalog: ReadonlyMap<string, Product>): Stock {
  return trip.items.reduce((current, item) => {
    const product = productOf(catalog, item.productId);
    return addLot(current, product, item.packages * product.packSize, trip.date);
  }, stock);
}

function wineWeeksOf(trips: readonly ShoppingTrip[], catalog: ReadonlyMap<string, Product>) {
  const withWine = trips.filter((trip) =>
    trip.items.some((item) => productOf(catalog, item.productId).tags?.includes('alcohol')),
  );
  return [...new Set(withWine.map((trip) => isoWeekStart(trip.date)))];
}

/**
 * DE: Übergang ab heute: Gekauftes minus Verbrauch der vergangenen Tage, in zeitlicher Reihenfolge.
 * EN: Hand-over from today: what was bought minus what past days used, in date order.
 */
export function carryOverAt(plan: Plan, request: PlanRequest, done: DoneState): CarryOver {
  const { catalog } = request.book;
  const pastDays = plan.days.filter((day) => day.date < done.today);
  const pastNeeds = planRequirements({ ...plan, days: pastDays }, request.recipes, {
    staples: request.staples,
    extra: done.reserved.filter((reserve) => reserve.date < done.today),
  });
  const pending = [...done.doneTrips].sort((a, b) => a.date.localeCompare(b.date));
  let stock = stockFromPantry(request.pantry, plan.period.start, catalog);
  for (const need of pastNeeds) {
    // DE: Nur Einkäufe bis zum Tag des Bedarfs können ihn decken.
    // EN: Only trips up to the day of the need can cover it.
    let next = pending[0];
    while (next && next.date <= need.date) {
      stock = addTrip(stock, next, catalog);
      pending.shift();
      next = pending[0];
    }
    stock = takeFromStock(stock, need.productId, need.quantity, need.date).stock;
  }
  stock = pending.reduce((current, trip) => addTrip(current, trip, catalog), stock);
  return {
    stock: stock.filter((lot) => lot.expires >= done.today),
    pastDays,
    wineWeeks: wineWeeksOf(done.doneTrips, catalog),
    reservedAhead: done.reserved.filter((reserve) => reserve.date >= done.today),
  };
}

/**
 * DE: Planungsanfrage für die Tage ab heute; feste Tage bleiben über `locked` erhalten.
 * EN: Planning request for the days from today; fixed days stay through `locked`.
 */
export function remainingRequest(plan: Plan, request: PlanRequest, done: DoneState): PlanRequest {
  const end = addDays(plan.period.start, plan.period.days);
  // DE: Gesperrte vergangene Tage liegen außerhalb des neuen Zeitraums und stören nicht.
  // EN: Locked past days lie outside the new period and do no harm.
  const locked = plan.days
    .filter((day) => day.dinner.locked)
    .map((day) => ({ date: day.date, recipeId: day.dinner.recipeId }));
  return {
    ...request,
    period: { start: done.today, days: daysBetween(done.today, end) },
    locked,
    earliestTrip: done.earliestTrip ?? done.today,
    // DE: Der Vorrat steckt schon im Übergang; ein zweites Mal würde er doppelt zählen.
    // EN: The pantry is already in the carry-over; adding it again would count it twice.
    pantry: {},
    carryOver: carryOverAt(plan, request, done),
  };
}

function combine(doneTrips: readonly ShoppingTrip[], rest?: ShoppingResult): ShoppingResult {
  const trips = [...doneTrips, ...(rest?.trips ?? [])];
  return {
    trips,
    ...shoppingTotals(trips),
    leftovers: rest?.leftovers ?? {},
    late: rest?.late ?? [],
  };
}

/**
 * DE: Einkäufe nach einer Änderung: erledigte bleiben, der Rest wird ab heute neu berechnet.
 * EN: Shopping after a change: done trips stay, the rest is recalculated from today.
 */
export function continueShopping(
  plan: Plan,
  request: PlanRequest,
  done: DoneState,
): ShoppingResult {
  const remaining = remainingRequest(plan, request, done);
  const future = {
    ...plan,
    period: remaining.period,
    days: plan.days.filter((day) => day.date >= done.today),
  };
  return combine(done.doneTrips, shopForPlan(future, remaining));
}

/**
 * DE: „Neu planen“ ab heute: vergangene Tage und erledigte Einkäufe bleiben unverändert.
 * EN: "Plan again" from today: past days and done trips stay unchanged.
 */
export function replanFrom(
  plan: Plan,
  request: PlanRequest,
  done: DoneState,
): { plan: Plan; shopping: ShoppingResult } {
  const remaining = remainingRequest(plan, request, done);
  if (remaining.period.days <= 0) return { plan, shopping: combine(done.doneTrips) };
  const result = generatePlan(remaining);
  const pastDays = plan.days.filter((day) => day.date < done.today);
  return {
    plan: {
      period: plan.period,
      days: [...pastDays, ...result.plan.days],
      warnings: result.plan.warnings,
    },
    shopping: combine(done.doneTrips, result.shopping),
  };
}
